import FileView from "./FileView";
import { assign, Sentinel } from "./common";

const ImageFileView = FileView.extend(
  /** @lends ImageFileView.prototype */
  {
    /**
     * View for an image file with local preview
     * @param {FileModel} model
     * @param {object} opts
     * @param {number[]} [opts.previewMaxSize=[150,150]] - maximum size of locally generated preview as [width, height]
     * @constructs
     * @extends FileView
     */
    constructor(model, opts) {
      FileView.call(this, model, opts);
      this.previewLocal();
    },

    constructDOM() {
      this.progressSentinel = new Sentinel();
      this.errorSentinel = new Sentinel();

      const zoomBtnHTML = this.opts.template.parse("zoom_btn", {
        id: this.id,
        icon_zoom: this.opts.icon_zoom
      });
      const ccwBtnHTML = this.opts.template.parse("ccw_btn", { id: this.id });
      const cwBtnHTML = this.opts.template.parse("cw_btn", { id: this.id });
      const delBtnHTML = this.opts.template.parse("del_btn", { id: this.id });

      this.setHTML(this.opts.template.parse("image_item", {
        id: this.id,
        progress: this.progressSentinel.string,
        error: this.errorSentinel.string,
        zoom_btn: zoomBtnHTML,
        ccw_btn: ccwBtnHTML,
        cw_btn: cwBtnHTML,
        del_btn: delBtnHTML
      }));

      this.progressView = this.createProgressView();
      this.errorView = this.createErrorView();
    },

    /**
     * Generate local preview. If initialized from File (e.g. from drag'n'drop) uses the FileReader API when available.
     * Otherwise sets src from {@link ImageFileView#model.url}
     * @protected
     */
    previewLocal() {
      if(this.model.isStatic()) {
        this.setPreviewUrl(this.model.url);
        return;
      }

      if(typeof window.FileReader !== "function") return; // FileReader not supported

      const reader = new FileReader();
      reader.onload = evt => {
        const img = new Image();
        img.src = evt.target.result;

        img.onload = () => {
          let { width, height } = img;
          const [maxWidth, maxHeight] = this.opts.previewMaxSize;

          if(width > maxWidth) {
            height = height * maxWidth / width;
            width = maxWidth;
          }

          if(height > maxHeight) {
            width = width * maxHeight / height;
            height = maxHeight;
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          this.setPreviewUrl(canvas.toDataURL());
        }
      };

      reader.readAsDataURL(this.model.file);
    },

    /**
     * Set preview src to image url
     * @protected
     */
    setPreviewUrl(url) {
      if(!url) return;

      const img = new Image();
      let didOnloadFire = false;

      const onload = () => {
        didOnloadFire = true;
        const img = this.getElement("image");
        if(!img) return; // the view is already destroyed
        img.style.backgroundImage = `url(${url})`;
      };

      img.onload = onload;
      img.src = url;

      // maybe there are some browsers, which don't fire onload for cached images, so just make sure
      if(img.complete && !didOnloadFire) { onload(); delete img.onload; }
    },

    /**
     * Handle rotation button click
     * @param {Event} evt
     * @param {boolean} isRight - rotate clockwise
     * @protected
     */
    handleRotClick(evt, isRight) {
      if(this.ignoreNextClick) {
        this.ignoreNextClick = false;
        return;
      }
      this.model.rotate(isRight);
    },

    onMount() {
      FileView.prototype.onMount.call(this);

      this.getElement("rotl").addEventListener("click", evt => { this.handleRotClick(evt, false); });

      this.getElement("rotr").addEventListener("click", evt => { this.handleRotClick(evt, true); });

      const zoomBtn = this.getElement("zoom");
      if(zoomBtn) zoomBtn.addEventListener("click", evt => { this.emit("zoom", { event: evt }); });

      this.setPreviewUrl(this.model.url);
      this.listenTo(this.model, "url", ({ url }) => { this.setPreviewUrl(url) });
    },

    getDefaultOptions() {
      return assign(FileView.prototype.getDefaultOptions(), {
        previewMaxSize: [150, 150]
      });
    }
  });

export default ImageFileView;
